import React from 'react';
import get from 'lodash/get';

export const FIELD_ALIGNMENT_SETTING_KEY = 'fieldAlignment';

export const FIELD_ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Middle' },
  { value: 'right', label: 'Right' },
];

// The canvas draws the preview box `width + 4` percent wide at `left - 2`, so
// the 2% of horizontal padding on each side sits outside the field itself.
const PREVIEW_PADDING = 4;

// Field types whose `options.width` is not a canvas percentage: a table stores
// the pixel width of its own grid, so it must never be measured back into one.
const UNMEASURABLE_TYPES = ['table'];

// `left`/`width` are percentages of the canvas. Dragging clamps `left` to
// [0, 100 - width], so alignment targets the same range the user can reach by
// hand.
export const getAlignedLeft = (alignment, width) => {
  if (typeof width !== 'number' || Number.isNaN(width)) return null;

  switch (alignment) {
    case 'left':
      return 0;
    case 'center':
      return (100 - width) / 2;
    case 'right':
      return 100 - width;
    default:
      return null;
  }
};

// The alignment a field placed at `left` is sitting at, or null when it is
// somewhere in between. Dragging a field is a placement by hand, so the stored
// alignment has to follow the drop instead of the field being pulled back to it.
export const getAlignmentForLeft = (left, width) => {
  if (typeof left !== 'number' || typeof width !== 'number') return null;

  const match = FIELD_ALIGNMENT_OPTIONS.find(({ value }) => getAlignedLeft(value, width) === left);

  return match ? match.value : null;
};

// The share of the canvas the field's preview box (padding included) wants, or
// null when the field is not on the canvas.
//
// The box is measured at `max-content` on purpose: while it is auto-sized it
// only gets the room between `left` and the edge of the page, so measuring it
// in place would report a narrower field the further right it already sits.
const measurePreviewWidth = (element) => {
  const area = document.getElementsByClassName('drop-area')[0];
  const preview = document.getElementsByClassName(`form-builder-element-${element.id}`)[0];

  if (!area?.offsetWidth || !preview) return null;

  const inlineWidth = preview.style.width;
  preview.style.width = 'max-content';
  const renderedWidth = (preview.offsetWidth / area.offsetWidth) * 100;
  preview.style.width = inlineWidth;

  return renderedWidth;
};

// A few field types never got a default `options.width` (Submitters info auto
// entry, the checkbox/radio lists, rating, link, file attach) and so shrink to
// fit their content — there is no width for `getAlignedLeft` to work from.
// Reads the width the field wants off the canvas, in `options.width` units, so
// it can be stored once and aligned like every other field afterwards.
//
// A field that already has a width is turned away: measuring it at
// `max-content` would report its content, not the width it was given.
export const measureFieldWidth = (element) => {
  if (!element?.id || UNMEASURABLE_TYPES.includes(element.type)) return null;
  if (typeof element.options?.width === 'number') return null;

  const renderedWidth = measurePreviewWidth(element);

  if (!renderedWidth) return null;

  // Rounded up so the content that was measured still fits once the width is
  // pinned, then held inside [1, 100] — a field wider than the page has nothing
  // left to align, and a hairline one must not be pushed off the right edge.
  return Math.min(Math.max(Math.ceil(renderedWidth) - PREVIEW_PADDING, 1), 100);
};

// A table is as wide as its columns make it, in pixels, and has no canvas width
// for `getAlignedLeft` to work from (see UNMEASURABLE_TYPES). Reads the width it
// is drawn at, in `options.width` units. Never stored — as `options.width` it
// would resize the question column — so it is read again on every alignment,
// which also keeps up with the table as its columns change.
export const measureTableWidth = (element) => {
  if (!element?.id || element.type !== 'table') return null;

  const renderedWidth = measurePreviewWidth(element);

  if (!renderedWidth) return null;

  // Rounded to a tenth of a percent, about a pixel of the page, so the same
  // table always lands on the same `left`; held inside [1, 100] like a measured
  // field, so one wider than the page is aligned to its left edge.
  return Math.min(Math.max(Math.round((renderedWidth - PREVIEW_PADDING) * 10) / 10, 1), 100);
};

// The width a field is aligned by: the one it stores, or for a table the one it
// is drawn at.
export const getAlignmentWidth = (element) => (element?.type === 'table' ? measureTableWidth(element) : element?.options?.width);

// Positions the field horizontally on the form canvas (distinct from the
// `alignment` option, which controls the label/text alignment inside the field).
const FieldAlignmentSetting = ({ element, onChange }) => {
  const value = get(element, `options[${FIELD_ALIGNMENT_SETTING_KEY}]`) || 'left';

  const handleChange = (e) => {
    if (!element?.id || !onChange) return;
    onChange(e.target.value, FIELD_ALIGNMENT_SETTING_KEY, element.id);
  };

  return (
    <div className="property-item">
      <h3 className="form-builder-aside-property-sub-title">Field Alignment</h3>
      <div className="form-group">
        {FIELD_ALIGNMENT_OPTIONS.map((option) => (
          <span className="form-radio-button" key={option.value}>
            <input type="radio" name="fieldAlignment" value={option.value} checked={value === option.value} onChange={handleChange} />
            <span>{option.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default FieldAlignmentSetting;
